// src/components/drawer/BulkSendManager.tsx

import {
  Add as AddIcon,
  Campaign as CampaignIcon,
  CheckCircle as CheckCircleIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  FileDownload as FileDownloadIcon,
  PlayCircle as PlayCircleIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  Send as SendIcon,
  StopCircle as StopCircleIcon,
  TextFields as TextFieldsIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputBase,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  Typography,
  styled,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAccount, useErrorHandler, useToast } from "../../../context";
import { useBulkJobsHistory } from "../../../hooks/useBulkJobsHistory";
import { CampaignTemplate, Template } from "../../../interfaces/all";
import {
  getAllCampaigns,
  getCampaignsWithTemplates,
  getTemplateParameters,
  getTemplates,
} from "../../../services/api";
import { apiService } from "../../../services/axios";
import socketService from "../../../services/socket";
import { ParameterConfig } from "../../../types/template-config";
import { ErrorHandler } from "../../../utils/error-handler";
import Header from "../../chat/Header/Header";
import TemplatePreview from "../templates/TemplatePreview";
import TemplateSelector from "../templates/TemplateSelector";

const Container = styled(Box)`
  width: 100%;
  height: 100%;
  background: #fff;
  border-right: 1px solid #e9edef;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 600px) {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }
`;

const ContentContainer = styled(Box)`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 60px);
`;

// Custom Step Connector
const CustomStepConnector = styled(StepConnector)(() => ({
  "&.MuiStepConnector-root": {
    top: 10,
    left: "calc(-50% + 20px)",
    right: "calc(50% + 20px)",
  },
  "&.MuiStepConnector-line": {
    borderTopWidth: 2,
    borderColor: "#e9edef",
  },
  "&.Mui-active .MuiStepConnector-line": {
    borderColor: "#00a884",
  },
  "&.Mui-completed .MuiStepConnector-line": {
    borderColor: "#00a884",
  },
}));

// Custom Step Icon
const CustomStepIcon = styled("div")<{
  ownerState: { active?: boolean; completed?: boolean };
}>(({ ownerState }) => ({
  backgroundColor: ownerState.completed
    ? "#00a884"
    : ownerState.active
      ? "#00a884"
      : "#e9edef",
  zIndex: 1,
  color: "#fff",
  width: 20,
  height: 20,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  fontSize: "12px",
  fontWeight: 600,
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
}));

export const BulkSendManager: React.FC = () => {
  const { account } = useAccount();
  const { showError, showToast } = useToast();
  const { handleError } = useErrorHandler();

  const {
    jobs: bulkJobs,
    loading,
    error,
    pagination,
    goToPreviousPage: hookGoToPreviousPage,
    goToNextPage: hookGoToNextPage,
    refresh: refreshJobsList,
  } = useBulkJobsHistory();

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);

  // Estados para filtros e busca
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateFilter, setDateFilter] = useState<string>("all");
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [responseMetrics, setResponseMetrics] = useState<{
    totalSent: number;
    responded: number;
    notResponded: number;
    positiveResponses: number;
    negativeResponses: number;
    blocklistResponses: number;
    hasInteractiveButtons: boolean;
  } | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsDownloadLoading, setMetricsDownloadLoading] = useState(false);
  const [crmSyncLoading, setCrmSyncLoading] = useState(false);

  // Estados para dados reais (campanhas e templates)
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [campaignTemplates, setCampaignTemplates] = useState<
    CampaignTemplate[]
  >([]);
  const [loadingData, setLoadingData] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);

  // Estados para seleção no modal
  const [selectedCampaign, setSelectedCampaign] = useState<string>("");
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null,
  );
  const [templateParameters, setTemplateParameters] = useState<any>(null);
  const [loadingParameters, setLoadingParameters] = useState(false);
  const [parameterConfigs, setParameterConfigs] = useState<ParameterConfig[]>(
    [],
  );
  const [attendantName, setAttendantName] = useState<string>("");

  const LEAD_STATUS_OPTIONS = useMemo(
    () => [
      { value: "PENDENTE", label: "PENDENTE" },
      {
        value: "CAIXA POSTAL / ENVIO DE MENSAGEM",
        label: "CAIXA POSTAL / ENVIO DE MENSAGEM",
      },
      {
        value: "NÃO COMPLETA LIGAÇÃO / ENVIO DE MENSAGEM",
        label: "NÃO COMPLETA LIGAÇÃO / ENVIO DE MENSAGEM",
      },
      { value: "AGENDAMENTO", label: "AGENDAMENTO" },
      { value: "INTERESSADO", label: "INTERESSADO" },
    ],
    [],
  );
  const [allowedStatuses, setAllowedStatuses] = useState<string[]>(() => [
    "PENDENTE",
    "CAIXA POSTAL / ENVIO DE MENSAGEM",
    "NÃO COMPLETA LIGAÇÃO / ENVIO DE MENSAGEM",
  ]);

  // Estados para envio
  const [isSending, setIsSending] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);

  // Cleanup adequado para evitar vazamentos de memória
  useEffect(() => {
    return () => {
      // Limpar estados que podem causar vazamentos
      setCampaigns([]);
      setTemplates([]);
      setCampaignTemplates([]);
      setSelectedCampaign("");
      setSelectedTemplate(null);
      setTemplateParameters(null);
      setParameterConfigs([]);
    };
  }, []);

  // Configurar listeners de socket para eventos de envio em massa
  useEffect(() => {
    const handleBulkJobCompleted = (data: any) => {
      console.log("✅ [BULK SEND MANAGER] Job completado:", data);

      if (data.jobId === currentJobId) {
        showToast({
          type: "success",
          message: `Envio em massa concluído! ${data.result.sent} de ${data.result.total} mensagens enviadas com sucesso.`,
        });

        setCurrentJobId(null);
        handleSendComplete();
      }
    };

    const handleBulkJobFailed = (data: any) => {
      console.log("❌ [BULK SEND MANAGER] Job falhou:", data);

      if (data.jobId === currentJobId) {
        showToast({
          type: "error",
          message: `Falha no envio em massa: ${data.error}`,
        });

        setCurrentJobId(null);
        handleSendError(data.error);
      }
    };

    // Registrar handlers
    socketService.setEventHandlers({
      onBulkJobCompleted: handleBulkJobCompleted,
      onBulkJobFailed: handleBulkJobFailed,
    });

    // Cleanup
    return () => {
      socketService.setEventHandlers({
        onBulkJobCompleted: undefined,
        onBulkJobFailed: undefined,
      });
    };
  }, [currentJobId, showToast]);

  // Função para carregar dados reais (campanhas e templates)
  const fetchData = useCallback(async () => {
    if (!account?.company_id) {
      setDataError("ID da empresa não encontrado");
      return;
    }

    try {
      setLoadingData(true);
      setDataError(null);

      const [templatesResponse, campaignsResponse, allCampaignsResponse] =
        await Promise.all([
          getTemplates(account.company_id),
          getCampaignsWithTemplates(),
          getAllCampaigns(),
        ]);

      if (
        templatesResponse.status === "success" &&
        templatesResponse.data?.data
      ) {
        setTemplates(templatesResponse.data.data as unknown as Template[]);
      }

      if (campaignsResponse.status === "success" && campaignsResponse.data) {
        setCampaignTemplates(
          campaignsResponse.data as unknown as CampaignTemplate[],
        );
      }

      if (
        allCampaignsResponse.status === "success" &&
        allCampaignsResponse.data
      ) {
        const campaignsData = allCampaignsResponse.data as any;
        if (campaignsData.campaigns) {
          setCampaigns(campaignsData.campaigns);
        }
      }
    } catch (err: unknown) {
      const errorResponse = ErrorHandler.handleApiError(err);
      const errorMessage =
        errorResponse.userMessage ||
        errorResponse.message ||
        "Erro ao carregar templates";
      setDataError(errorMessage);
      handleError(err);
    } finally {
      setLoadingData(false);
    }
  }, [account?.company_id, handleError]);

  // Carregar configuração pré-definida do template
  const loadPredefinedTemplateConfig = async (templateId: string) => {
    if (!account?.company_id) {
      console.log("Company ID não encontrado");
      return null;
    }

    try {
      const response = await apiService.get(
        `/template-configs/${templateId}?companyId=${account.company_id}`,
      );
      if (
        response.status === "success" &&
        response.data &&
        typeof response.data === "object" &&
        response.data !== null &&
        "parameters" in response.data
      ) {
        return (response.data as { parameters: any[] }).parameters || [];
      }
    } catch (error) {
      console.log(
        "Nenhuma configuração pré-definida encontrada para este template",
      );
    }
    return null;
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenDialog = () => {
    setOpenDialog(true);
    // Reset form states
    setSelectedCampaign("");
    setSelectedTemplate(null);
    setTemplateParameters(null);
    setParameterConfigs([]);
    setAttendantName("");
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogLoading(false);
  };

  // Callbacks para seleção de campanha e template
  const handleCampaignChange = useCallback((campaignId: string) => {
    setSelectedCampaign(campaignId);
    setSelectedTemplate(null);
    setTemplateParameters(null);
    setParameterConfigs([]);
  }, []);

  const handleTemplateSelect = useCallback(
    async (template: Template) => {
      setSelectedTemplate(template);
      try {
        setLoadingParameters(true);

        // Carregar configuração pré-definida
        const predefinedConfig = await loadPredefinedTemplateConfig(
          template.id,
        );

        const response = await getTemplateParameters(
          template.id,
          template.name,
          account?.company_id,
        );

        if (response.status === "success" && response.data) {
          setTemplateParameters(response.data);

          // Se há configuração pré-definida, aplicar ela
          if (predefinedConfig && predefinedConfig.length > 0) {
            const configs: ParameterConfig[] = [];
            for (let i = 0; i < predefinedConfig.length; i++) {
              const param = predefinedConfig[i];
              let type: "fixed" | "dynamic" = "dynamic";
              let value = "";
              let dynamicField: ParameterConfig["dynamicField"] = "clientName";

              // Se param é um objeto (estrutura salva)
              if (typeof param === "object" && param !== null) {
                type = param.type || "dynamic";
                value = param.value || "";
                dynamicField = param.dynamicField ?? "clientName";
              } else if (
                typeof param === "string" &&
                param.startsWith("{{") &&
                param.endsWith("}}")
              ) {
                // Se param é uma string com placeholder
                type = "dynamic";
                const placeholderMatch = param.match(/\{\{(\d+)\}\}/);
                if (placeholderMatch) {
                  const placeholderNumber = parseInt(placeholderMatch[1]);
                  switch (placeholderNumber) {
                    case 1:
                      dynamicField = "clientName";
                      break;
                    case 2:
                      dynamicField = "clientPhone";
                      break;
                    case 3:
                      dynamicField = "companyName";
                      break;
                    case 4:
                      dynamicField = "attendantName";
                      break;
                    case 5:
                      dynamicField = "scheduledDate";
                      break;
                    default:
                      dynamicField = "clientName";
                  }
                }
              } else if (typeof param === "string") {
                // Se param é uma string fixa
                type = "fixed";
                value = param;
              }

              configs.push({
                index: i,
                type,
                dynamicField,
                value,
              });
            }
            setParameterConfigs(configs);
          } else {
            // Se não há configuração pré-definida, inicializar com valores padrão
            const templateData = response.data as any;
            if (templateData.requiredParametersCount > 0) {
              const configs: ParameterConfig[] = [];
              for (let i = 0; i < templateData.requiredParametersCount; i++) {
                configs.push({
                  index: i,
                  type: "dynamic",
                  value: "",
                  dynamicField: "clientName",
                });
              }
              setParameterConfigs(configs);
            } else {
              setParameterConfigs([]);
            }
          }
        } else {
          setTemplateParameters(null);
        }
      } catch (err) {
        console.error("Erro ao buscar parâmetros do template:", err);
        setTemplateParameters(null);
        handleError(err);
      } finally {
        setLoadingParameters(false);
      }
    },
    [account?.company_id, handleError],
  );

  const handleSendComplete = useCallback(() => {
    showToast({
      type: "success",
      message: "Template enviado com sucesso!",
    });

    // Reset form
    setSelectedCampaign("");
    setSelectedTemplate(null);
    setTemplateParameters(null);
    setParameterConfigs([]);
    setAttendantName("");
    handleCloseDialog();
  }, [showToast]);

  const handleSendError = useCallback(
    (errorMessage: string) => {
      showError(errorMessage);
    },
    [showError],
  );

  // Validação para habilitar o botão
  const validateRequiredFields = useCallback(() => {
    if (!selectedCampaign || !selectedTemplate) return false;
    if (selectedTemplate.status !== "APPROVED") return false;

    // Se não há parâmetros obrigatórios, está válido
    if (
      !templateParameters ||
      templateParameters.requiredParametersCount === 0
    ) {
      return true;
    }

    // Validar se todos os parâmetros obrigatórios estão configurados
    if (parameterConfigs.length !== templateParameters.requiredParametersCount)
      return false;

    // Verificar se cada configuração está válida
    for (const config of parameterConfigs) {
      if (config.type === "fixed" && !config.value.trim()) return false;
      if (config.type === "dynamic" && !config.dynamicField) return false;
    }

    return true;
  }, [
    selectedCampaign,
    selectedTemplate,
    templateParameters,
    parameterConfigs,
  ]);

  const isSendButtonEnabled = validateRequiredFields() && !isSending;

  // Calcular step atual para o stepper
  const getCurrentStep = () => {
    if (!selectedCampaign) return 0;
    if (!selectedTemplate) return 1;
    if (selectedTemplate && templateParameters) return 2;
    return 3;
  };

  const currentStep = getCurrentStep();

  // Definir steps
  const steps = [
    { label: "Campanha", icon: <CampaignIcon sx={{ fontSize: 12 }} /> },
    { label: "Template", icon: <TextFieldsIcon sx={{ fontSize: 12 }} /> },
    { label: "Preview", icon: <CheckIcon sx={{ fontSize: 12 }} /> },
  ];

  // Componente de Step Icon customizado
  const StepIconComponent = ({
    active,
    completed,
    icon,
  }: {
    active?: boolean;
    completed?: boolean;
    icon: React.ReactNode;
  }) => {
    return (
      <CustomStepIcon ownerState={{ active, completed }}>
        {completed ? <CheckIcon sx={{ fontSize: 12 }} /> : icon}
      </CustomStepIcon>
    );
  };

  // Filtrar jobs baseado nos filtros ativos
  const filteredJobs = bulkJobs.filter((job) => {
    // Filtro por texto de busca
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      const templateName = job.templateName.toLowerCase();
      const campaignName = (job.campaignName || "").toLowerCase();
      const sentBy = job.sentBy.toLowerCase();

      if (
        !templateName.includes(searchLower) &&
        !campaignName.includes(searchLower) &&
        !sentBy.includes(searchLower)
      ) {
        return false;
      }
    }

    // Filtro por status
    if (statusFilter !== "all" && job.status !== statusFilter) {
      return false;
    }

    // Filtro por data
    if (dateFilter !== "all") {
      const jobDate = new Date(job.createdAt);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      switch (dateFilter) {
        case "today": {
          const jobToday = new Date(
            jobDate.getFullYear(),
            jobDate.getMonth(),
            jobDate.getDate(),
          );

          if (jobToday.getTime() !== today.getTime()) return false;
          break;
        }

        case "week": {
          const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

          if (jobDate < weekAgo) return false;
          break;
        }

        case "month": {
          const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

          if (jobDate < monthAgo) return false;
          break;
        }
      }
    }

    return true;
  });

  // Função para mostrar detalhes do job
  const handleShowDetails = (job: any) => {
    setSelectedJob(job);
    setShowDetails(true);
    setResponseMetrics(null);

    if (job?.id) {
      setMetricsLoading(true);
      apiService
        .get(`/bulk-queue/jobs/${job.id}/metrics`)
        .then((response) => {
          if (response?.status === "success" && response.data) {
            const metrics = response.data as {
              totalSent: number;
              responded: number;
              notResponded: number;
              positiveResponses: number;
              negativeResponses: number;
              blocklistResponses: number;
              hasInteractiveButtons: boolean;
            };
            setResponseMetrics(metrics);
          }
        })
        .catch((error) => {
          handleError(error);
        })
        .finally(() => {
          setMetricsLoading(false);
        });
    }
  };

  const handleDownloadJobMetrics = async () => {
    if (!selectedJob?.id) {
      return;
    }

    setMetricsDownloadLoading(true);
    try {
      const response = await apiService.getRaw<Blob>(
        `/bulk-queue/jobs/${selectedJob.id}/metrics/export`,
        {
          responseType: "blob",
        },
      );

      if (response?.data) {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const safeCampaignName = (
          selectedJob.campaignName || selectedJob.id
        ).replace(/[\\/:*?"<>|]+/g, "-");
        const link = document.createElement("a");
        link.href = url;
        link.download = `metricas-envio-massa-${safeCampaignName}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);

        showToast({
          type: "success",
          message: "Arquivo de métricas baixado com sucesso.",
        });
      }
    } catch (error) {
      handleError(error);
    } finally {
      setMetricsDownloadLoading(false);
    }
  };

  const handleSyncCrmFromResponses = async () => {
    if (!selectedJob?.id) {
      return;
    }

    setCrmSyncLoading(true);
    try {
      const response = await apiService.post(
        `/bulk-queue/jobs/${selectedJob.id}/responses/sync-crm`,
      );

      if (response?.status === "success") {
        const data = response.data as any;
        showToast({
          type: "success",
          message: `CRM atualizado. Leads afetados: ${data.updatedLeads || 0}. Não encontrados no CRM: ${data.notFoundInCrm || 0}.`,
        });
      } else {
        showError("Não foi possível sincronizar as respostas com o CRM agora.");
      }

      // Recarregar métricas após a sincronização (para refletir eventuais mudanças)
      setMetricsLoading(true);
      setResponseMetrics(null);
      apiService
        .get(`/bulk-queue/jobs/${selectedJob.id}/metrics`)
        .then((metricsResponse) => {
          if (metricsResponse?.status === "success" && metricsResponse.data) {
            setResponseMetrics(metricsResponse.data as any);
          }
        })
        .catch((error) => {
          handleError(error);
        })
        .finally(() => {
          setMetricsLoading(false);
        });
    } catch (error) {
      handleError(error);
    } finally {
      setCrmSyncLoading(false);
    }
  };

  // Função para fechar detalhes
  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedJob(null);
    setResponseMetrics(null);
  };

  // Função para ir para página anterior
  const goToPreviousPage = () => {
    hookGoToPreviousPage();
  };

  // Função para ir para próxima página
  const goToNextPage = () => {
    hookGoToNextPage();
  };

  // Formatar data
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("pt-BR");
  };

  // Formatar duração
  const formatDuration = (duration?: number) => {
    if (!duration) return "-";

    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Função para obter texto do status
  const getStatusText = (status: string) => {
    switch (status) {
      case "pending":
        return "Pendente";
      case "processing":
        return "Processando";
      case "completed":
        return "Concluído";
      case "failed":
        return "Falhou";
      case "cancelled":
        return "Cancelado";
      default:
        return status;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <ScheduleIcon sx={{ color: "#f57c00" }} />;
      case "processing":
        return <PlayCircleIcon sx={{ color: "#1976d2" }} />;
      case "completed":
        return <CheckCircleIcon sx={{ color: "#2e7d32" }} />;
      case "failed":
        return <ErrorIcon sx={{ color: "#d32f2f" }} />;
      case "cancelled":
        return <StopCircleIcon sx={{ color: "#d32f2f" }} />;
      default:
        return <ScheduleIcon sx={{ color: "#666" }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "warning";
      case "processing":
        return "primary";
      case "completed":
        return "success";
      case "failed":
        return "error";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ height: "60px", flexShrink: 0 }}>
          <Header title="Envio em Massa" />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      {/* Header com botão Novo Envio */}
      <Box sx={{ height: "60px", flexShrink: 0, position: "relative" }}>
        <Header title="Envio em Massa" />
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{
            position: "absolute",
            top: "50%",
            right: 16,
            transform: "translateY(-50%)",
            px: 1.5,
            py: 0.5,
            fontSize: "11px",
            borderRadius: "4px",
            backgroundColor: "#00a884",
            textTransform: "none",
            fontWeight: 500,
            minWidth: "auto",
            height: "28px",
            "&:hover": {
              backgroundColor: "#008f6f",
              transform: "translateY(-50%) scale(1.02)",
            },
            transition: "all 0.2s ease-in-out",
            "& .MuiButton-startIcon": {
              marginRight: "4px",
              fontSize: "14px",
            },
          }}
        >
          Novo Envio
        </Button>
      </Box>

      {/* Conteúdo principal */}
      <ContentContainer>
        {/* Erro de Carregamento de Dados (Templates/Campanhas) */}
        {dataError && (
          <Alert severity="error" sx={{ m: 2, borderRadius: "8px" }}>
            {dataError}
          </Alert>
        )}

        {/* Erro de Histórico de Jobs */}
        {error && (
          <Alert severity="error" sx={{ m: 2, borderRadius: "8px" }}>
            {error}
          </Alert>
        )}

        {/* Loading interno */}
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Filtros avançados */}
        {!loading && (
          <Box
            sx={{
              p: 2,
              backgroundColor: "#f0f2f5",
              borderBottom: "1px solid #e9edef",
              flexShrink: 0,
            }}
          >
            <Stack spacing={2}>
              {/* Campo de busca */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "4px 8px",
                  border: "1px solid #e9edef",
                  "&:focus-within": {
                    borderColor: "#00a884",
                    boxShadow: "0 0 0 2px rgba(0, 168, 132, 0.1)",
                  },
                }}
              >
                <SearchIcon sx={{ color: "#54656f", fontSize: 20, mr: 1 }} />
                <InputBase
                  placeholder="Buscar por template, campanha ou usuário..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  sx={{
                    flex: 1,
                    fontSize: "14px",
                    color: "#111b21",
                    "&::placeholder": {
                      color: "#667781",
                    },
                  }}
                />
              </Box>

              {/* Filtros em linha */}
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                {/* Filtro por Status */}
                <FormControl size="small" sx={{ flex: 1 }}>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    displayEmpty
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "6px",
                      fontSize: "13px",
                      width: "100%",
                      "& .MuiSelect-select": {
                        padding: "8px 12px",
                        fontSize: "13px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e9edef",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00a884",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00a884",
                      },
                    }}
                  >
                    <MenuItem value="all">Todos os Status</MenuItem>
                    <MenuItem value="pending">Pendente</MenuItem>
                    <MenuItem value="processing">Processando</MenuItem>
                    <MenuItem value="completed">Concluído</MenuItem>
                    <MenuItem value="failed">Falhou</MenuItem>
                    <MenuItem value="cancelled">Cancelado</MenuItem>
                  </Select>
                </FormControl>

                {/* Filtro por Data */}
                <FormControl size="small" sx={{ flex: 1 }}>
                  <Select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    displayEmpty
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "6px",
                      fontSize: "13px",
                      width: "100%",
                      "& .MuiSelect-select": {
                        padding: "8px 12px",
                        fontSize: "13px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e9edef",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00a884",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00a884",
                      },
                    }}
                  >
                    <MenuItem value="all">Todas as Datas</MenuItem>
                    <MenuItem value="today">Hoje</MenuItem>
                    <MenuItem value="week">Última Semana</MenuItem>
                    <MenuItem value="month">Último Mês</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Stack>
          </Box>
        )}

        {/* Lista de envios */}
        {!loading && (
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              p: 0,
              width: "100%",
              minWidth: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {filteredJobs.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                  flexDirection: "column",
                  gap: 2,
                  width: "100%",
                  minWidth: "100%",
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  📭 Nenhum envio encontrado
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {bulkJobs.length === 0
                    ? "Os envios em massa aparecerão aqui quando forem criados"
                    : searchText.trim() ||
                        statusFilter !== "all" ||
                        dateFilter !== "all"
                      ? "Nenhum envio encontrado com os filtros aplicados"
                      : "Nenhum envio encontrado"}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  p: 1,
                  gap: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  flex: 1,
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#c1c1c1",
                    borderRadius: "3px",
                    "&:hover": {
                      background: "#a8a8a8",
                    },
                  },
                }}
              >
                {filteredJobs.map((job) => (
                  <Paper
                    key={job.id}
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      border: "1px solid #00a884",
                      borderRadius: "8px",
                      width: "100%",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                      "&:hover": {
                        backgroundColor: "#f5f6f6",
                        borderColor: "#00a884",
                        boxShadow: "0 2px 8px rgba(0, 168, 132, 0.1)",
                      },
                    }}
                    onClick={() => handleShowDetails(job)}
                  >
                    {/* Header do card */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1.5,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        {getStatusIcon(job.status)}
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: "#111b21",
                              fontSize: "13px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "200px",
                            }}
                          >
                            {job.templateName}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#667781",
                              fontSize: "11px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "200px",
                            }}
                          >
                            {job.campaignName || `Campanha ${job.campaignId}`}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        label={getStatusText(job.status)}
                        color={getStatusColor(job.status)}
                        size="small"
                        sx={{ fontSize: "11px", height: "24px" }}
                      />
                    </Box>

                    {/* Informações principais */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        {/* Progresso */}
                        <Box sx={{ minWidth: 60, maxWidth: 80, flex: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#667781",
                              fontSize: "11px",
                              display: "block",
                              mb: 0.5,
                            }}
                          >
                            {job.progress.sent}/{job.progress.total}
                          </Typography>
                          <Box
                            sx={{
                              width: "100%",
                              height: 6,
                              backgroundColor: "#e9edef",
                              borderRadius: 3,
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                width: `${(job.progress.sent / job.progress.total) * 100}%`,
                                height: "100%",
                                backgroundColor:
                                  job.status === "completed"
                                    ? "#00a884"
                                    : "#008f6f",
                                transition: "width 0.3s ease",
                              }}
                            />
                          </Box>
                        </Box>

                        {/* Taxa de sucesso */}
                        {job.successRate !== undefined && (
                          <Typography
                            variant="caption"
                            sx={{
                              color:
                                job.successRate >= 80
                                  ? "#00a884"
                                  : job.successRate >= 60
                                    ? "#ff9800"
                                    : "#f44336",
                              fontSize: "10px",
                              fontWeight: 500,
                            }}
                          >
                            {job.successRate}%
                          </Typography>
                        )}
                      </Box>

                      {/* Data */}
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#667781",
                          fontSize: "10px",
                          textAlign: "right",
                        }}
                      >
                        {formatDate(job.createdAt)}
                      </Typography>
                    </Box>

                    {/* Footer com informações adicionais */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pt: 1,
                        borderTop: "1px solid #f0f2f5",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#667781",
                          fontSize: "10px",
                        }}
                      >
                        Enviado por: {job.sentBy}
                      </Typography>

                      {job.duration && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#667781",
                            fontSize: "10px",
                          }}
                        >
                          Duração: {formatDuration(job.duration)}
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Paginação */}
        {!loading && pagination.totalPages > 0 && (
          <Box
            sx={{
              p: 2,
              textAlign: "center",
              borderTop: "1px solid #f0f0f0",
              backgroundColor: "#fff",
              position: "sticky",
              bottom: 0,
              zIndex: 11,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <button
                onClick={goToPreviousPage}
                disabled={loading || pagination.currentPage <= 1}
                style={{
                  background:
                    pagination.currentPage <= 1 || loading
                      ? "#f5f5f5"
                      : "#00a884",
                  color:
                    pagination.currentPage <= 1 || loading ? "#999" : "white",
                  border:
                    pagination.currentPage <= 1 || loading
                      ? "1px solid #e0e0e0"
                      : "none",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  cursor:
                    pagination.currentPage <= 1 || loading
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "12px",
                  fontWeight: "500",
                  transition: "all 0.2s ease",
                  opacity: pagination.currentPage <= 1 || loading ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                onMouseOver={(e) => {
                  if (pagination.currentPage > 1 && !loading) {
                    e.currentTarget.style.backgroundColor = "#008f6f";
                  }
                }}
                onFocus={(e) => {
                  if (pagination.currentPage > 1 && !loading) {
                    e.currentTarget.style.backgroundColor = "#008f6f";
                  }
                }}
                onMouseOut={(e) => {
                  if (pagination.currentPage > 1 && !loading) {
                    e.currentTarget.style.backgroundColor = "#00a884";
                  }
                }}
                onBlur={(e) => {
                  if (pagination.currentPage > 1 && !loading) {
                    e.currentTarget.style.backgroundColor = "#00a884";
                  }
                }}
              >
                {loading && pagination.currentPage > 1 ? (
                  <>
                    <CircularProgress size={12} sx={{ color: "white" }} />
                    Carregando...
                  </>
                ) : (
                  "← Anterior"
                )}
              </button>

              <span
                style={{ fontSize: "12px", color: "#666", padding: "0 8px" }}
              >
                {pagination.currentPage || 1} de {pagination.totalPages || 1}
              </span>

              <button
                onClick={goToNextPage}
                disabled={
                  loading ||
                  (pagination.currentPage || 1) >= (pagination.totalPages || 1)
                }
                style={{
                  background:
                    (pagination.currentPage || 1) >=
                      (pagination.totalPages || 1) || loading
                      ? "#f5f5f5"
                      : "#00a884",
                  color:
                    (pagination.currentPage || 1) >=
                      (pagination.totalPages || 1) || loading
                      ? "#999"
                      : "white",
                  border:
                    (pagination.currentPage || 1) >=
                      (pagination.totalPages || 1) || loading
                      ? "1px solid #e0e0e0"
                      : "none",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  cursor:
                    (pagination.currentPage || 1) >=
                      (pagination.totalPages || 1) || loading
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "12px",
                  fontWeight: "500",
                  transition: "all 0.2s ease",
                  opacity:
                    (pagination.currentPage || 1) >=
                      (pagination.totalPages || 1) || loading
                      ? 0.6
                      : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                onMouseOver={(e) => {
                  if (
                    (pagination.currentPage || 1) <
                      (pagination.totalPages || 1) &&
                    !loading
                  ) {
                    e.currentTarget.style.backgroundColor = "#008f6f";
                  }
                }}
                onFocus={(e) => {
                  if (
                    (pagination.currentPage || 1) <
                      (pagination.totalPages || 1) &&
                    !loading
                  ) {
                    e.currentTarget.style.backgroundColor = "#008f6f";
                  }
                }}
                onMouseOut={(e) => {
                  if (
                    (pagination.currentPage || 1) <
                      (pagination.totalPages || 1) &&
                    !loading
                  ) {
                    e.currentTarget.style.backgroundColor = "#00a884";
                  }
                }}
                onBlur={(e) => {
                  if (
                    (pagination.currentPage || 1) <
                      (pagination.totalPages || 1) &&
                    !loading
                  ) {
                    e.currentTarget.style.backgroundColor = "#00a884";
                  }
                }}
              >
                {loading &&
                (pagination.currentPage || 1) < (pagination.totalPages || 1) ? (
                  <>
                    <CircularProgress size={12} sx={{ color: "white" }} />
                    Carregando...
                  </>
                ) : (
                  "Próximo →"
                )}
              </button>
            </Box>
          </Box>
        )}
      </ContentContainer>

      {/* Modal de Novo Envio */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={dialogLoading}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              border: "1px solid rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(10px)",
              maxWidth: "90vw",
              maxHeight: "90vh",
              overflow: "hidden",
              animation: "modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "@keyframes modalSlideIn": {
                "0%": {
                  opacity: 0,
                  transform: "scale(0.9) translateY(-20px)",
                },
                "100%": {
                  opacity: 1,
                  transform: "scale(1) translateY(0)",
                },
              },
              // Ajustes para mobile
              "@media (max-width: 600px)": {
                maxWidth: "95vw",
                maxHeight: "95vh",
                margin: "8px",
                borderRadius: "8px",
              },
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            borderBottom: "1px solid #e9edef",
            background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            position: "relative",
          }}
        >
          {/* Elemento decorativo sutil */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 120,
              height: 120,
              background:
                "radial-gradient(circle, rgba(0, 168, 132, 0.05) 0%, transparent 70%)",
              borderRadius: "50%",
              transform: "translate(30px, -30px)",
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 48,
                height: 48,
                borderRadius: "12px",
                backgroundColor: "#e8f5e8",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.8)",
              }}
            >
              <SendIcon sx={{ color: "#00a884", fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#111b21",
                  mb: 0.5,
                  fontSize: "18px",
                }}
              >
                Novo Envio em Massa
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#667781",
                  fontSize: "14px",
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                Configure e envie mensagens para múltiplos contatos
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, overflow: "hidden" }}>
          {/* Stepper de Progresso */}
          <Box
            sx={{
              p: 3,
              pb: 2,
              borderBottom: "1px solid #f0f2f5",
              backgroundColor: "#fafafa",
            }}
          >
            <Stepper
              activeStep={currentStep}
              connector={<CustomStepConnector />}
              sx={{
                "& .MuiStepLabel-root": {
                  padding: 0,
                },
                "& .MuiStepLabel-label": {
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#667781",
                  "&.Mui-active": {
                    color: "#00a884",
                    fontWeight: 600,
                  },
                  "&.Mui-completed": {
                    color: "#00a884",
                    fontWeight: 600,
                  },
                },
              }}
            >
              {steps.map((step) => (
                <Step key={step.label}>
                  <StepLabel
                    StepIconComponent={(props) => (
                      <StepIconComponent {...props} icon={step.icon} />
                    )}
                  >
                    {step.label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Box
            sx={{
              maxHeight: "50vh",
              overflowY: "auto",
              p: 3,
              pb: 4, // Padding inferior extra para evitar sobreposição
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555",
              },
              // Ajustes para mobile
              "@media (max-width: 600px)": {
                p: 2,
                pb: 3,
                maxHeight: "60vh", // Mais altura em mobile para acomodar layout vertical
              },
            }}
          >
            {/* Layout em duas colunas: Seleção + Preview */}
            <Box
              sx={{
                mb: 3,
                "@media (max-width: 600px)": {
                  mb: 2, // Menos margem em mobile
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "flex-start",
                  flexDirection: { xs: "column", sm: "row" }, // Coluna em mobile, linha em desktop
                  "@media (max-width: 600px)": {
                    gap: 1.5, // Menor gap em mobile
                  },
                }}
              >
                {/* Coluna da Esquerda: Seleção */}
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <CampaignIcon sx={{ color: "#00a884", fontSize: 18 }} />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: "#111b21",
                        fontSize: "14px",
                      }}
                    >
                      Selecionar Campanha e Template
                    </Typography>
                    {selectedCampaign && selectedTemplate && (
                      <Chip
                        icon={<CheckIcon sx={{ fontSize: 12 }} />}
                        label="Concluído"
                        size="small"
                        color="success"
                        sx={{ fontSize: "10px", height: "20px" }}
                      />
                    )}
                  </Box>

                  {loadingData ? (
                    <Card
                      sx={{
                        borderRadius: "8px",
                        border: "1px solid #00a884",
                        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
                        p: 3,
                        textAlign: "center",
                      }}
                    >
                      <CircularProgress
                        size={24}
                        sx={{ color: "#00a884", mb: 2 }}
                      />
                      <Typography variant="body2" sx={{ color: "#667781" }}>
                        Carregando campanhas e templates...
                      </Typography>
                    </Card>
                  ) : dataError ? (
                    <Card
                      sx={{
                        borderRadius: "8px",
                        border: "1px solid #ffcdd2",
                        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
                        p: 3,
                        backgroundColor: "#ffebee",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "#c62828", textAlign: "center" }}
                      >
                        ⚠️ {dataError}
                      </Typography>
                    </Card>
                  ) : (
                    <Card
                      sx={{
                        borderRadius: "8px",
                        border: "1px solid #00a884",
                        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
                      }}
                    >
                      <TemplateSelector
                        campaigns={campaigns}
                        templates={templates}
                        campaignTemplates={campaignTemplates}
                        selectedCampaign={selectedCampaign}
                        selectedTemplate={selectedTemplate}
                        loading={loadingData}
                        error={dataError}
                        onCampaignChange={handleCampaignChange}
                        onTemplateSelect={handleTemplateSelect}
                      />
                    </Card>
                  )}

                  {/* Filtro de status do lead (CRM) */}
                  <Card
                    sx={{
                      mt: 2,
                      borderRadius: "8px",
                      border: "1px solid #00a884",
                      boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
                      p: 2,
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: "#111b21", mb: 0.5 }}
                    >
                      Status do lead (para envio)
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        multiple
                        value={allowedStatuses}
                        onChange={(event) => {
                          const value = event.target
                            .value as unknown as string[];
                          setAllowedStatuses(value);
                        }}
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {(selected as string[]).map((value) => (
                              <Chip
                                key={value}
                                label={value}
                                size="small"
                                sx={{ height: "22px", fontSize: "11px" }}
                              />
                            ))}
                          </Box>
                        )}
                      >
                        {LEAD_STATUS_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            <Checkbox
                              checked={allowedStatuses.indexOf(opt.value) > -1}
                            />
                            <Typography
                              variant="body2"
                              sx={{ color: "#111b21" }}
                            >
                              {opt.label}
                            </Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Card>
                </Box>

                {/* Coluna da Direita: Preview */}
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <TextFieldsIcon sx={{ color: "#00a884", fontSize: 18 }} />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: "#111b21",
                        fontSize: "14px",
                      }}
                    >
                      Preview do Template
                    </Typography>
                    {selectedTemplate && templateParameters && (
                      <Chip
                        icon={<CheckIcon sx={{ fontSize: 12 }} />}
                        label="Pronto para Envio"
                        size="small"
                        color="success"
                        sx={{ fontSize: "10px", height: "20px" }}
                      />
                    )}
                  </Box>

                  {loadingParameters ? (
                    <Card
                      sx={{
                        borderRadius: "8px",
                        border: "1px solid #00a884",
                        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
                        p: 3,
                        textAlign: "center",
                      }}
                    >
                      <CircularProgress
                        size={24}
                        sx={{ color: "#00a884", mb: 2 }}
                      />
                      <Typography variant="body2" sx={{ color: "#667781" }}>
                        Carregando parâmetros do template...
                      </Typography>
                    </Card>
                  ) : selectedTemplate && templateParameters ? (
                    <Card
                      sx={{
                        borderRadius: "8px",
                        border: "1px solid #00a884",
                        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
                      }}
                    >
                      <TemplatePreview
                        selectedTemplate={selectedTemplate}
                        templateParameters={templateParameters}
                        parameterConfigs={parameterConfigs}
                        attendantName={attendantName}
                      />
                    </Card>
                  ) : (
                    <Card
                      sx={{
                        borderRadius: "8px",
                        border: "1px solid #00a884",
                        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
                        p: 3,
                        textAlign: "center",
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#667781",
                          fontStyle: "italic",
                        }}
                      >
                        Selecione um template para ver o preview
                      </Typography>
                    </Card>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Configuração de Parâmetros */}
            {selectedTemplate &&
              templateParameters &&
              templateParameters.requiredParametersCount > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Card
                    sx={{
                      borderRadius: "8px",
                      border: "1px solid #e9edef",
                      boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
                      p: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 2, fontWeight: 600, color: "#111b21" }}
                    >
                      Configuração de Parâmetros
                    </Typography>
                    {loadingParameters ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 2,
                        }}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: "#667781" }}>
                        {templateParameters.requiredParametersCount}{" "}
                        parâmetro(s) necessário(s). Configure os parâmetros
                        antes de enviar.
                      </Typography>
                    )}
                  </Card>
                </Box>
              )}
          </Box>

          {isSending && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderTop: "1px solid #e9edef",
                backgroundColor: "rgba(0, 168, 132, 0.06)",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <CircularProgress size={20} sx={{ color: "#00a884" }} />
              <Typography
                variant="body2"
                sx={{ color: "#00a884", fontWeight: 500 }}
              >
                Envio em massa está sendo gerado. Aguarde...
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            borderTop: "1px solid #e9edef",
            background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Button
            onClick={handleCloseDialog}
            disabled={isSending}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.2,
              fontSize: "13px",
              color: "#667781",
              border: "1px solid #e9edef",
              backgroundColor: "#fff",
              "&:hover": {
                backgroundColor: "#f0f2f5",
                borderColor: "#d1d7db",
              },
              "&:disabled": {
                opacity: 0.6,
                cursor: "not-allowed",
              },
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            Fechar
          </Button>

          <Button
            variant="contained"
            disabled={!isSendButtonEnabled}
            startIcon={
              isSending ? (
                <CircularProgress size={16} sx={{ color: "white" }} />
              ) : (
                <SendIcon />
              )
            }
            onClick={async () => {
              if (!selectedCampaign || !selectedTemplate) {
                showError("Por favor, selecione uma campanha e um template");
                return;
              }

              if (!allowedStatuses || allowedStatuses.length === 0) {
                showError("Selecione ao menos um status de lead para envio.");
                return;
              }

              if (selectedTemplate.status !== "APPROVED") {
                showError("O template selecionado não está aprovado");
                return;
              }

              try {
                setIsSending(true);
                showToast({
                  type: "info",
                  message: "Gerando envio em massa...",
                });

                // Preparar parâmetros finais seguindo a lógica do BulkSender
                const finalParameters = parameterConfigs.map((config) => {
                  if (config.type === "fixed") {
                    return config.value;
                  } else {
                    // Para modo dinâmico, enviar o valor real baseado no campo dinâmico
                    switch (config.dynamicField) {
                      case "clientName":
                        return "CLIENT_NAME_PLACEHOLDER";
                      case "clientPhone":
                        return "CLIENT_PHONE_PLACEHOLDER";
                      case "companyName":
                        return "COMPANY_NAME_PLACEHOLDER";
                      case "attendantName":
                        return "ATTENDANT_NAME_PLACEHOLDER";
                      case "scheduledDate":
                        return "SCHEDULED_DATE_PLACEHOLDER";
                      default:
                        return "CLIENT_NAME_PLACEHOLDER";
                    }
                  }
                });

                // Preparar dados para envio seguindo a estrutura do BulkSender
                const sendData = {
                  campaignId: selectedCampaign,
                  templateId: selectedTemplate.id,
                  templateName: selectedTemplate.name,
                  parameters: finalParameters,
                  sentBy: attendantName || account?.username || "Admin",
                  allowedStatuses: allowedStatuses,
                };

                console.log("Enviando template:", sendData);

                // Estimar volume baseado na campanha (seguindo lógica do BulkSender)
                const isLargeVolume =
                  selectedCampaign.includes("large") ||
                  selectedCampaign.includes("massive");

                // Fazer chamada real para a API de envio em massa
                const response = await (isLargeVolume
                  ? apiService.postLargeBulk("/bulk-queue/start", sendData)
                  : apiService.postBulk("/bulk-queue/start", sendData));

                if (
                  response &&
                  response.status === "success" &&
                  response.data &&
                  typeof response.data === "object" &&
                  response.data !== null &&
                  "jobId" in response.data
                ) {
                  const { jobId } = response.data as { jobId: string };

                  setCurrentJobId(jobId);
                  showToast({
                    type: "success",
                    message:
                      "Envio em massa iniciado. A lista será atualizada.",
                  });
                  refreshJobsList();
                  handleCloseDialog();
                } else {
                  throw new Error("Erro ao iniciar envio em massa");
                }
              } catch (error) {
                console.error("Erro ao enviar:", error);
                const errorResponse = ErrorHandler.handleApiError(error);
                const errorMessage =
                  errorResponse.userMessage ||
                  errorResponse.message ||
                  "Erro ao enviar template";
                handleSendError(errorMessage);
              } finally {
                setIsSending(false);
              }
            }}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.2,
              fontSize: "13px",
              backgroundColor: "#00a884",
              boxShadow: "0 2px 8px rgba(0, 168, 132, 0.3)",
              "&:hover": {
                backgroundColor: "#008f6f",
                boxShadow: "0 4px 12px rgba(0, 168, 132, 0.4)",
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
                boxShadow: "none",
                transform: "none",
              },
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            {isSending ? "Enviando..." : "Enviar Template"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Detalhes do Job */}
      <Dialog
        open={showDetails}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
              overflow: "hidden",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            borderBottom: "1px solid #e9edef",
            background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            position: "relative",
          }}
        >
          {/* Elemento decorativo sutil */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 120,
              height: 120,
              background:
                "radial-gradient(circle, rgba(0, 168, 132, 0.05) 0%, transparent 70%)",
              borderRadius: "50%",
              transform: "translate(30px, -30px)",
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              position: "relative",
              zIndex: 1,
            }}
          >
            {selectedJob && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor:
                    selectedJob.status === "completed"
                      ? "#e8f5e8"
                      : selectedJob.status === "processing"
                        ? "#e3f2fd"
                        : selectedJob.status === "failed"
                          ? "#ffebee"
                          : selectedJob.status === "pending"
                            ? "#fff3e0"
                            : "#f5f5f5",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.8)",
                }}
              >
                {selectedJob && getStatusIcon(selectedJob.status)}
              </Box>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#111b21",
                  mb: 0.5,
                  fontSize: "18px",
                }}
              >
                {selectedJob?.templateName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#667781",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {selectedJob?.campaignName ||
                  `Campanha ${selectedJob?.campaignId}`}
              </Typography>
            </Box>
            {selectedJob && (
              <Chip
                label={getStatusText(selectedJob.status)}
                color={getStatusColor(selectedJob.status)}
                size="small"
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  height: "28px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                }}
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedJob && (
            <Box>
              {/* Seção de Progresso */}
              <Box sx={{ mb: 2.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                    mt: 1,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#111b21",
                      mb: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    📊 Progresso do Envio
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#ffffff",
                    borderRadius: "8px",
                    border: "1px solid rgb(0, 168, 132)",
                    boxShadow: "0 1px 6px rgba(0, 168, 132, 0.05)",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Elemento decorativo sutil */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: -10,
                      right: -10,
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      background:
                        "radial-gradient(circle, rgba(0, 168, 132, 0.04) 0%, transparent 70%)",
                      opacity: 0.4,
                    }}
                  />

                  <Box sx={{ position: "relative", zIndex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#111b21",
                          fontSize: "13px",
                        }}
                      >
                        Mensagens Enviadas
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 700,
                          color: "#00a884",
                          fontSize: "16px",
                        }}
                      >
                        {selectedJob.progress.sent} /{" "}
                        {selectedJob.progress.total}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        width: "100%",
                        height: 6,
                        backgroundColor: "#f0f2f5",
                        borderRadius: 3,
                        overflow: "hidden",
                        mb: 1.5,
                        boxShadow: "inset 0 1px 2px rgba(0, 168, 132, 0.06)",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${(selectedJob.progress.sent / selectedJob.progress.total) * 100}%`,
                          height: "100%",
                          background:
                            selectedJob.status === "completed"
                              ? "linear-gradient(90deg, #00a884 0%, #00c896 100%)"
                              : "linear-gradient(90deg, #008f6f 0%, #00a884 100%)",
                          transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                          borderRadius: 3,
                          boxShadow: "0 1px 2px rgba(0, 168, 132, 0.2)",
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(70px, 1fr))",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 1,
                          backgroundColor: "rgba(0, 168, 132, 0.08)",
                          borderRadius: "6px",
                          border: "1px solid rgba(0, 168, 132, 0.15)",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: "#00a884",
                            fontWeight: 700,
                            fontSize: "16px",
                            mb: 0.25,
                          }}
                        >
                          {selectedJob.progress.sent}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#00a884",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.3px",
                            fontSize: "9px",
                          }}
                        >
                          Enviadas
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          textAlign: "center",
                          p: 1,
                          backgroundColor: "rgba(244, 67, 54, 0.08)",
                          borderRadius: "6px",
                          border: "1px solid rgba(244, 67, 54, 0.15)",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: "#f44336",
                            fontWeight: 700,
                            fontSize: "16px",
                            mb: 0.25,
                          }}
                        >
                          {selectedJob.progress.failed}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#f44336",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.3px",
                            fontSize: "9px",
                          }}
                        >
                          Falharam
                        </Typography>
                      </Box>

                      {selectedJob.successRate !== undefined && (
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 1,
                            backgroundColor:
                              selectedJob.successRate >= 80
                                ? "rgba(0, 168, 132, 0.08)"
                                : selectedJob.successRate >= 60
                                  ? "rgba(255, 152, 0, 0.08)"
                                  : "rgba(244, 67, 54, 0.08)",
                            borderRadius: "6px",
                            border: `1px solid ${
                              selectedJob.successRate >= 80
                                ? "rgba(0, 168, 132, 0.15)"
                                : selectedJob.successRate >= 60
                                  ? "rgba(255, 152, 0, 0.15)"
                                  : "rgba(244, 67, 54, 0.15)"
                            }`,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color:
                                selectedJob.successRate >= 80
                                  ? "#00a884"
                                  : selectedJob.successRate >= 60
                                    ? "#ff9800"
                                    : "#f44336",
                              fontWeight: 700,
                              fontSize: "16px",
                              mb: 0.25,
                            }}
                          >
                            {selectedJob.successRate}%
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color:
                                selectedJob.successRate >= 80
                                  ? "#00a884"
                                  : selectedJob.successRate >= 60
                                    ? "#ff9800"
                                    : "#f44336",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.3px",
                              fontSize: "9px",
                            }}
                          >
                            Sucesso
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Seção de Respostas dos Clientes */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#111b21",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    💬 Respostas dos Clientes
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
                      onClick={handleSyncCrmFromResponses}
                      disabled={
                        crmSyncLoading || metricsLoading || !selectedJob?.id
                      }
                      sx={{ borderRadius: "8px" }}
                    >
                      {crmSyncLoading ? "Atualizando..." : "Atualizar CRM"}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<FileDownloadIcon sx={{ fontSize: 18 }} />}
                      onClick={handleDownloadJobMetrics}
                      disabled={
                        metricsDownloadLoading ||
                        metricsLoading ||
                        !selectedJob?.id
                      }
                      sx={{ borderRadius: "8px" }}
                    >
                      {metricsDownloadLoading
                        ? "baixando..."
                        : "download resumo"}
                    </Button>
                  </Box>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#ffffff",
                    borderRadius: "8px",
                    border: "1px solid #00a884",
                    boxShadow: "0 1px 6px rgba(0, 168, 132, 0.05)",
                  }}
                >
                  {metricsLoading && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" sx={{ color: "#667781" }}>
                        Carregando métricas de resposta...
                      </Typography>
                    </Box>
                  )}

                  {!metricsLoading && responseMetrics && (
                    <>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(140px, 1fr))",
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 1.5,
                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                            borderRadius: "6px",
                            border: "1px solid rgba(25, 118, 210, 0.15)",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: "#1976d2",
                              fontWeight: 700,
                              fontSize: "16px",
                              mb: 0.25,
                            }}
                          >
                            {responseMetrics.responded}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#1976d2",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.3px",
                              fontSize: "9px",
                            }}
                          >
                            Respondidas
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            textAlign: "center",
                            p: 1.5,
                            backgroundColor: "rgba(158, 158, 158, 0.08)",
                            borderRadius: "6px",
                            border: "1px solid rgba(158, 158, 158, 0.2)",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: "#667781",
                              fontWeight: 700,
                              fontSize: "16px",
                              mb: 0.25,
                            }}
                          >
                            {responseMetrics.notResponded}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#667781",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.3px",
                              fontSize: "9px",
                            }}
                          >
                            Não Respondidas
                          </Typography>
                        </Box>

                        {responseMetrics.hasInteractiveButtons && (
                          <Box
                            sx={{
                              textAlign: "center",
                              p: 1.5,
                              backgroundColor: "rgba(46, 125, 50, 0.08)",
                              borderRadius: "6px",
                              border: "1px solid rgba(46, 125, 50, 0.15)",
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{
                                color: "#2e7d32",
                                fontWeight: 700,
                                fontSize: "16px",
                                mb: 0.25,
                              }}
                            >
                              {responseMetrics.positiveResponses}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#2e7d32",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.3px",
                                fontSize: "9px",
                              }}
                            >
                              Respostas Positivas
                            </Typography>
                          </Box>
                        )}

                        {responseMetrics.hasInteractiveButtons && (
                          <Box
                            sx={{
                              textAlign: "center",
                              p: 1.5,
                              backgroundColor: "rgba(211, 47, 47, 0.08)",
                              borderRadius: "6px",
                              border: "1px solid rgba(211, 47, 47, 0.15)",
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{
                                color: "#d32f2f",
                                fontWeight: 700,
                                fontSize: "16px",
                                mb: 0.25,
                              }}
                            >
                              {responseMetrics.negativeResponses}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#d32f2f",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.3px",
                                fontSize: "9px",
                              }}
                            >
                              Respostas Negativas
                            </Typography>
                          </Box>
                        )}

                        {responseMetrics.hasInteractiveButtons && (
                          <Box
                            sx={{
                              textAlign: "center",
                              p: 1.5,
                              backgroundColor: "rgba(96, 125, 139, 0.08)",
                              borderRadius: "6px",
                              border: "1px solid rgba(96, 125, 139, 0.2)",
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{
                                color: "#455a64",
                                fontWeight: 700,
                                fontSize: "16px",
                                mb: 0.25,
                              }}
                            >
                              {responseMetrics.blocklistResponses}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#455a64",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.3px",
                                fontSize: "9px",
                              }}
                            >
                              Blocklist
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {!responseMetrics.hasInteractiveButtons && (
                        <Typography
                          variant="body2"
                          sx={{ color: "#667781", mt: 1.5 }}
                        >
                          Template sem botões interativos. Métricas de resposta
                          positiva/negativa não se aplicam.
                        </Typography>
                      )}
                    </>
                  )}

                  {!metricsLoading && !responseMetrics && (
                    <Typography variant="body2" sx={{ color: "#667781" }}>
                      Métricas de resposta indisponíveis para este envio.
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Seção de Informações */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: "#111b21",
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  ℹ️ Informações do Job
                </Typography>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#ffffff",
                    borderRadius: "8px",
                    border: "1px solid rgb(0, 168, 132)",
                    boxShadow: "0 1px 6px rgba(0, 168, 132, 0.05)",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        border: "1px solid #e9edef",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#667781",
                          fontSize: "11px",
                          textTransform: "uppercase",
                          fontWeight: 500,
                          letterSpacing: "0.5px",
                        }}
                      >
                        Enviado por
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "#111b21",
                          mt: 0.5,
                        }}
                      >
                        {selectedJob.sentBy}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        border: "1px solid #e9edef",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#667781",
                          fontSize: "11px",
                          textTransform: "uppercase",
                          fontWeight: 500,
                          letterSpacing: "0.5px",
                        }}
                      >
                        Criado em
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "#111b21",
                          mt: 0.5,
                        }}
                      >
                        {formatDate(selectedJob.createdAt)}
                      </Typography>
                    </Box>

                    {selectedJob.startedAt && (
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: "#f8f9fa",
                          borderRadius: "8px",
                          border: "1px solid #e9edef",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#667781",
                            fontSize: "11px",
                            textTransform: "uppercase",
                            fontWeight: 500,
                            letterSpacing: "0.5px",
                          }}
                        >
                          Iniciado em
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: "#111b21",
                            mt: 0.5,
                          }}
                        >
                          {formatDate(selectedJob.startedAt)}
                        </Typography>
                      </Box>
                    )}

                    {selectedJob.completedAt && (
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: "#f8f9fa",
                          borderRadius: "8px",
                          border: "1px solid #e9edef",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#667781",
                            fontSize: "11px",
                            textTransform: "uppercase",
                            fontWeight: 500,
                            letterSpacing: "0.5px",
                          }}
                        >
                          Concluído em
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: "#111b21",
                            mt: 0.5,
                          }}
                        >
                          {formatDate(selectedJob.completedAt)}
                        </Typography>
                      </Box>
                    )}

                    {selectedJob.duration && (
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: "#f8f9fa",
                          borderRadius: "8px",
                          border: "1px solid #e9edef",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#667781",
                            fontSize: "11px",
                            textTransform: "uppercase",
                            fontWeight: 500,
                            letterSpacing: "0.5px",
                          }}
                        >
                          Duração
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: "#111b21",
                            mt: 0.5,
                          }}
                        >
                          {formatDuration(selectedJob.duration)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Seção de Erro */}
              {selectedJob.error && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#111b21",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    ⚠️ Erro
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#ffebee",
                      borderRadius: "8px",
                      border: "1px solid #00a884",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#c62828",
                        fontFamily: "monospace",
                        fontSize: "13px",
                      }}
                    >
                      {selectedJob.error}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Seção de Conteúdo do Template */}
              {selectedJob.templateContent && (
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#111b21",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    📄 Conteúdo do Template
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #00a884",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "13px",
                        lineHeight: 1.6,
                        color: "#111b21",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {selectedJob.templateContent}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            borderTop: "1px solid #e9edef",
            background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            justifyContent: "center",
          }}
        >
          <Button
            onClick={handleCloseDetails}
            variant="contained"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.2,
              fontSize: "13px",
              backgroundColor: "#00a884",
              boxShadow: "0 2px 8px rgba(0, 168, 132, 0.3)",
              "&:hover": {
                backgroundColor: "#008f6f",
                boxShadow: "0 4px 12px rgba(0, 168, 132, 0.4)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BulkSendManager;
